import { lang } from "../../utilities/lang";
import { Voucher } from "../../assets/Voucher";
import { useState } from "react";
import { parseLocaleNumber, plausibleEvent } from "../../helpers/helpers";
import { useCart } from "../../context/CartContext";
import { useGetVoucher, usePurchase } from "../../api/useCart";
import { useSessionContext } from "../../context/SessionContext";
import { useListHosting } from "../../api/useHosting";
import { isArray } from "lodash";

export const CartSummary = () => {
    const [domains, voucher, setVoucher, send_invoice, setSendInvoice, clear] = useCart((state) => [
        state.domains,
        state.voucher,
        state.setVoucher,
        state.send_invoice,
        state.setSendInvoice,
        state.clear,
    ]);
    const { session } = useSessionContext();

    const [voucherState, setVoucherState] = useState<boolean>(!!voucher);
    const [voucherBuffer, setVoucherBuffer] = useState<string | undefined>(voucher);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [invoiceSent, setInvoiceSent] = useState<boolean>(false);

    const voucherQuery = useGetVoucher(voucher);
    const voucherData = voucherQuery.isSuccess ? voucherQuery.data.percent_off : null;
    const hostingQuery = useListHosting();
    const hostingData = hostingQuery.isSuccess ? hostingQuery.data.data : [];

    const purchase = usePurchase();

    const getCost = () => {
        const domainPrices = domains.map((domain) => parseLocaleNumber(domain.price));
        const hostingPrices = domains.map((domain) => {
            if (domain.hosting) {
                const hosting = hostingData.find((h) => h.id.toString() === domain.hosting);
                if (hosting) {
                    return (
                        (hosting.price_discounted ?? hosting.price) / 100 +
                        (hosting.setup_price_discounted ?? hosting.setup_price) / 100
                    );
                }
            }
            return 0;
        });
        domainPrices.push(...hostingPrices);
        if (domainPrices.length === 0) {
            return { total: 0, discount: 0, tax: 0 };
        } else {
            const total = domainPrices.reduce((a, b) => a + b);
            const discount = (total / 100) * (voucherData || 0) * -1;
            const tax = (total + discount) * 0.19;
            return { total: total, discount: discount, tax: tax };
        }
    };

    return (
        <div className={"cart-summary right card"}>
            {invoiceSent ? (
                <div className={"invoice"}>
                    <h2>{lang.get("Rechnung gesendet")}</h2>
                    <div>
                        {lang.get(
                            "Die Rechnung wurde an Ihre Email Adresse versandt. Die ausgewählten Domains werden bei Zahlungseingang registiert.",
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <h2>{lang.get("Zusammenfassung")}</h2>
                    <div className={"items-number"}>
                        {lang.get("Anzahl")}: {domains.length}
                    </div>
                    <div className={"voucher-wrapper"}>
                        <button
                            className={"voucher-button"}
                            onClick={() => {
                                plausibleEvent("add-voucher");
                                setVoucherState(true);
                            }}
                        >
                            <Voucher />
                            {lang.get("Ich habe einen Gutschein")}
                        </button>
                        {voucherState ? (
                            <div className={"voucher-code-wrapper"}>
                                <input
                                    className={"voucher-code"}
                                    type={"text"}
                                    value={voucherBuffer}
                                    disabled={!session}
                                    placeholder={!session ? lang.get("Bitte einloggen") : ""}
                                    onChange={(e) => {
                                        setVoucherBuffer(e.currentTarget.value.replace(/\W/g, ""));
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            setVoucher(voucherBuffer);
                                        }
                                    }}
                                    onBlur={() => {
                                        setVoucher(voucherBuffer);
                                    }}
                                />
                                <button
                                    className={`remove-voucher ${voucherQuery.isFetching ? "loading" : ""}`}
                                    onClick={() => {
                                        plausibleEvent("remove-voucher");
                                        setVoucher(undefined);
                                        setVoucherBuffer(undefined);
                                        setVoucherState(false);
                                    }}
                                ></button>
                            </div>
                        ) : null}
                    </div>
                    <ul className={"checkout-positions"}>
                        <li className={"total checkout-position"}>
                            <span>{lang.get("Gesamt")}</span>
                            <span>
                                {Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(
                                    getCost().total,
                                )}
                            </span>
                        </li>
                        {voucherData ? (
                            <li className={"voucher checkout-position"}>
                                <span>
                                    {lang.get("Rabatt")} {voucherData}%
                                </span>
                                <span>
                                    {Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(
                                        getCost().discount,
                                    )}
                                </span>
                            </li>
                        ) : null}
                        <li className={"tax checkout-position"}>
                            <span>{lang.get("Steuern")} 19%</span>
                            <span>
                                {Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(
                                    getCost().tax,
                                )}
                            </span>
                        </li>
                    </ul>
                    <div className={"sum"}>
                        <span>{lang.get("Summe")}</span>
                        <span>
                            {Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(
                                getCost().tax + getCost().discount + getCost().total,
                            )}
                        </span>
                    </div>
                    {session && session.verified ? (
                        <>
                            <h4>{lang.get("Zahlungsmethode")}</h4>
                            <label className={"payment-method"} aria-label={lang.get("Kreditkarte")}>
                                <input
                                    type={"radio"}
                                    name={"payment-method"}
                                    checked={!send_invoice}
                                    onChange={() => {
                                        setSendInvoice(false);
                                    }}
                                />
                            </label>
                            <label className={"payment-method"} aria-label={lang.get("PayPal, EPS, Klarna")}>
                                <input
                                    type={"radio"}
                                    name={"payment-method"}
                                    checked={send_invoice}
                                    onChange={() => {
                                        setSendInvoice(true);
                                    }}
                                />
                            </label>
                            <button
                                className={`button red ${loading ? "loading" : ""}`}
                                onClick={async () => {
                                    plausibleEvent("order-domains", domains.map((d) => d.name).join(","));
                                    setLoading(true);
                                    setError("");
                                    try {
                                        const data = await purchase.mutateAsync({
                                            domains: domains,
                                            voucher: voucher,
                                            send_invoice: send_invoice,
                                        });
                                        if (data && data.checkoutUrl) {
                                            window.open(data.checkoutUrl, "_self");
                                        } else if (data && data.checkoutUrl === null) {
                                            clear();
                                            setInvoiceSent(true);
                                        }
                                    } catch (error) {
                                        if (error.response.data.errors && isArray(error.response.data.errors)) {
                                            setError(error.response.data.errors[0]);
                                        }
                                    }
                                    setLoading(false);
                                }}
                            >
                                {lang.get("Bezahlen")}
                            </button>
                        </>
                    ) : !session ? (
                        <div className={"button-group"}>
                            <a href={`${lang.get("login.url")}?from=cart`} className={"button red"}>
                                {lang.get("Einloggen")}
                            </a>
                            <a href={`${lang.get("register.url")}?from=cart`} className={"button red"}>
                                {lang.get("Registrieren")}
                            </a>
                        </div>
                    ) : (
                        <button className={"button red"} disabled={true}>
                            {lang.get("Email Verifizieren")}
                        </button>
                    )}
                    {error ? <div className={"error"}>{error}</div> : null}
                </>
            )}
        </div>
    );
};
